<template>
  <span :class="badgeClasses">
    <slot name="icon" />
    <slot>{{ text }}</slot>
    <button
      v-if="dismissible"
      type="button"
      class="ml-1 -mr-1 hover:opacity-70 transition-opacity"
      @click="$emit('dismiss')"
    >
      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
export type BadgeSize = 'sm' | 'md' | 'lg'

export interface BadgeProps {
  /** Texto do badge */
  text?: string
  /** Variante de cor */
  variant?: BadgeVariant
  /** Tamanho */
  size?: BadgeSize
  /** Se pode ser fechado */
  dismissible?: boolean
  /** Estilo outline (sem fundo) */
  outline?: boolean
  /** Formato pill (mais arredondado) */
  pill?: boolean
}

const props = withDefaults(defineProps<BadgeProps>(), {
  variant: 'default',
  size: 'md',
  dismissible: false,
  outline: false,
  pill: false,
})

defineEmits<{
  dismiss: []
}>()

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm',
}

const variantClasses = computed(() => {
  const solid: Record<BadgeVariant, string> = {
    default: 'bg-grey-100 text-subtitle-gray',
    primary: 'bg-royal-25 text-primary',
    success: 'bg-green-50 text-success',
    warning: 'bg-yellow-50 text-warning',
    error: 'bg-red-50 text-error',
    info: 'bg-blue-50 text-blue-600',
  }

  const outline: Record<BadgeVariant, string> = {
    default: 'border border-grey-200 text-subtitle-gray bg-transparent',
    primary: 'border border-primary text-primary bg-transparent',
    success: 'border border-success text-success bg-transparent',
    warning: 'border border-warning text-warning bg-transparent',
    error: 'border border-error text-error bg-transparent',
    info: 'border border-blue-600 text-blue-600 bg-transparent',
  }

  return props.outline ? outline[props.variant] : solid[props.variant]
})

const badgeClasses = computed(() => [
  'inline-flex items-center font-medium',
  sizeClasses[props.size],
  variantClasses.value,
  props.pill ? 'rounded-full' : 'rounded-md',
])
</script>

